import { useEffect, useState } from 'react'
import { Modal } from '@/shared/components/ui'
import { useTranslation } from '@/shared/contexts/TranslationContext'
import type { AccountingClient } from '@/modules/accounting/clients/accountingClientTypes'
import type { StoredDocument } from '@/modules/documents'
import {
  confirmOutgoingBookkeeping,
  hasBlockingBookkeepingWarnings,
  prepareOutgoingBookkeeping,
  type OutgoingBookkeepingPreview,
} from '@/modules/accounting/bookkeeping/accountingBookkeepingService'

interface OutgoingBookkeepingModalProps {
  officeUnitId: string
  client: AccountingClient
  document: StoredDocument | null
  isOpen: boolean
  onClose: () => void
  onSaved: () => void
}

const valueFrom = (record: Record<string, unknown>, path: string) => {
  let value: unknown = record
  for (const segment of path.split('.')) {
    if (!value || typeof value !== 'object') return null
    value = (value as Record<string, unknown>)[segment]
  }
  return value
}

export function OutgoingBookkeepingModal({
  officeUnitId,
  client,
  document,
  isOpen,
  onClose,
  onSaved,
}: OutgoingBookkeepingModalProps) {
  const { t, locale } = useTranslation()
  const [preview, setPreview] = useState<OutgoingBookkeepingPreview | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const errorMessage = (value: any, fallbackKey: string) => {
    const code = typeof value?.message === 'string' ? value.message : ''
    if (code) {
      const translated = t(`accounting.bookkeeping.errors.${code}`)
      if (!translated.startsWith('accounting.')) return translated
    }
    return t(fallbackKey)
  }

  useEffect(() => {
    if (!isOpen || !document) return
    let cancelled = false
    setPreview(null)
    setError(null)
    setLoading(true)

    prepareOutgoingBookkeeping(officeUnitId, client, document)
      .then((result) => { if (!cancelled) setPreview(result) })
      .catch((loadError: any) => {
        console.error('[OutgoingBookkeepingModal] Preview error:', loadError)
        if (!cancelled) setError(errorMessage(loadError, 'accounting.bookkeeping.errors.preview'))
      })
      .finally(() => { if (!cancelled) setLoading(false) })

    return () => { cancelled = true }
  }, [client, document, isOpen, officeUnitId, t])

  const formatMoney = (value: unknown) => typeof value === 'number'
    ? new Intl.NumberFormat(locale, { style: 'currency', currency: 'BRL' }).format(value)
    : '—'
  const formatRate = (value: unknown) => typeof value === 'number'
    ? `${new Intl.NumberFormat(locale, { maximumFractionDigits: 4 }).format(value)}%`
    : '—'
  const taxCell = (item: Record<string, unknown>, tax: 'icms' | 'ipi' | 'pis' | 'cofins') => (
    <div className="min-w-28 space-y-0.5 text-xs">
      <p>CST {String(valueFrom(item, `attrs.cst_${tax}`) || '—')}</p>
      <p>BC {formatMoney(valueFrom(item, `attrs.${tax}_base`))}</p>
      <p>{formatRate(valueFrom(item, `attrs.${tax}_rate`))} · {formatMoney(valueFrom(item, `attrs.${tax}_amount`))}</p>
    </div>
  )
  const hasBlockingWarning = preview
    ? hasBlockingBookkeepingWarnings(preview.warnings)
    : false

  const handleConfirm = async () => {
    if (!preview || hasBlockingWarning || saving) return
    setSaving(true)
    setError(null)
    try {
      await confirmOutgoingBookkeeping(preview)
      onSaved()
      onClose()
    } catch (saveError: any) {
      console.error('[OutgoingBookkeepingModal] Bookkeeping error:', saveError)
      setError(errorMessage(saveError, 'accounting.bookkeeping.errors.save'))
    } finally {
      setSaving(false)
    }
  }

  const movementAttrs = preview?.movement.attrs && typeof preview.movement.attrs === 'object'
    ? preview.movement.attrs as Record<string, unknown>
    : {}

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('accounting.bookkeeping.outgoing.title')}
      size="lg"
      onSubmit={handleConfirm}
      submitLabel={t('accounting.bookkeeping.outgoing.confirm')}
      submitting={saving || loading || !preview || Boolean(hasBlockingWarning)}
      closeLabel={t('actions.close')}
    >
      {loading ? (
        <div className="py-12 text-center text-sm text-slate-500 dark:text-slate-400">
          {t('accounting.bookkeeping.outgoing.loading')}
        </div>
      ) : error && !preview ? (
        <p className="rounded-lg bg-rose-50 p-4 text-sm text-rose-700 dark:bg-rose-900/20 dark:text-rose-300">{error}</p>
      ) : preview ? (
        <div className="space-y-5">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-900 dark:bg-emerald-950/30">
            <p className="font-semibold text-emerald-800 dark:text-emerald-200">{t('accounting.bookkeeping.outgoing.directCopy')}</p>
            <p className="mt-1 text-sm text-emerald-700 dark:text-emerald-300">{t('accounting.bookkeeping.outgoing.directCopyHint')}</p>
          </div>

          <dl className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <div><dt className="text-xs text-slate-500">{t('accounting.clientDetails.columns.documentDate')}</dt><dd className="mt-1 font-semibold text-slate-900 dark:text-white">{String(preview.movement.movement_date || '—')}</dd></div>
            <div><dt className="text-xs text-slate-500">{t('accounting.clientDetails.columns.number')}</dt><dd className="mt-1 font-semibold text-slate-900 dark:text-white">{String(movementAttrs.document_number || '—')}</dd></div>
            <div><dt className="text-xs text-slate-500">{t('accounting.bookkeeping.taxes.icms')}</dt><dd className="mt-1 font-semibold text-slate-900 dark:text-white">{formatMoney(movementAttrs.icms_amount)}</dd></div>
            <div><dt className="text-xs text-slate-500">{t('accounting.bookkeeping.taxes.ipi')}</dt><dd className="mt-1 font-semibold text-slate-900 dark:text-white">{formatMoney(movementAttrs.ipi_amount)}</dd></div>
            <div><dt className="text-xs text-slate-500">{t('accounting.bookkeeping.taxes.pis')}</dt><dd className="mt-1 font-semibold text-slate-900 dark:text-white">{formatMoney(movementAttrs.pis_amount)}</dd></div>
            <div><dt className="text-xs text-slate-500">{t('accounting.bookkeeping.taxes.cofins')}</dt><dd className="mt-1 font-semibold text-slate-900 dark:text-white">{formatMoney(movementAttrs.cofins_amount)}</dd></div>
          </dl>

          <div>
            <div className="flex items-center justify-between gap-3"><h3 className="font-semibold text-slate-900 dark:text-white">{t('accounting.bookkeeping.outgoing.items')}</h3><span className="text-xs text-slate-500">{preview.items.length}</span></div>
            {preview.items.length === 0 ? (
              <p className="mt-3 rounded-lg bg-slate-50 p-4 text-sm text-slate-500 dark:bg-slate-900 dark:text-slate-400">{t('accounting.bookkeeping.outgoing.totalOnly')}</p>
            ) : (
              <div className="mt-3 overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700">
                <table className="min-w-full divide-y divide-slate-200 text-sm dark:divide-slate-700">
                  <thead className="bg-slate-50 text-left text-xs text-slate-500 dark:bg-slate-900"><tr><th className="px-3 py-2">#</th><th className="px-3 py-2">{t('accounting.bookkeeping.outgoing.item')}</th><th className="px-3 py-2">CFOP</th><th className="px-3 py-2">ICMS</th><th className="px-3 py-2">IPI</th><th className="px-3 py-2">PIS</th><th className="px-3 py-2">COFINS</th></tr></thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">{preview.items.slice(0, 20).map((item, index) => <tr key={String(item.line_number || index)}><td className="px-3 py-2">{String(item.line_number || index + 1)}</td><td className="max-w-64 truncate px-3 py-2" title={String(item.description || '')}>{String(item.description || item.external_code || '—')}</td><td className="px-3 py-2">{String(valueFrom(item, 'attrs.cfop') || '—')}</td><td className="px-3 py-2">{taxCell(item, 'icms')}</td><td className="px-3 py-2">{taxCell(item, 'ipi')}</td><td className="px-3 py-2">{taxCell(item, 'pis')}</td><td className="px-3 py-2">{taxCell(item, 'cofins')}</td></tr>)}</tbody>
                </table>
                {preview.items.length > 20 && <p className="border-t border-slate-200 px-3 py-2 text-xs text-slate-500 dark:border-slate-700">{t('accounting.bookkeeping.outgoing.itemsLimited', { count: String(preview.items.length) })}</p>}
              </div>
            )}
          </div>

          <p className="text-xs text-slate-500">{t('accounting.bookkeeping.outgoing.layoutUsed', { layout: preview.layoutName, version: preview.mappingVersion })}</p>
          {preview.warnings.length > 0 && <div className={`rounded-lg p-4 text-sm ${hasBlockingWarning ? 'bg-rose-50 text-rose-700 dark:bg-rose-900/20 dark:text-rose-300' : 'bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300'}`}>{t(hasBlockingWarning ? 'accounting.bookkeeping.errors.invalidItems' : 'accounting.bookkeeping.outgoing.warnings', { count: String(preview.warnings.length) })}</div>}
          {error && <p className="rounded-lg bg-rose-50 p-4 text-sm text-rose-700 dark:bg-rose-900/20 dark:text-rose-300">{error}</p>}
        </div>
      ) : null}
    </Modal>
  )
}
